import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { notificationsAPI } from '../services/api';
import './NotificationDropdown.css';

interface Notification {
  id: number;
  user_id: number;
  type: 'friend_request' | 'friend_accepted' | 'temp_location_shared' | 'friend_nearby' | 'friend_in_same_city';
  title: string;
  message: string;
  link?: string;
  related_user_id?: number;
  related_resource_id?: number;
  is_read: boolean;
  created_at: string;
  related_user_first_name?: string;
  related_user_last_name?: string;
  related_user_profile_photo?: string;
}

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadCountChange: (count: number) => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ isOpen, onClose, onUnreadCountChange }) => {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [swipeState, setSwipeState] = useState<{
    id: number | null;
    offset: number;
    direction: 'left' | 'right' | null;
  }>({ id: null, offset: 0, direction: null });
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking on the notification button itself
      if (target.closest('.notification-button')) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationsAPI.getAll(10);
      setNotifications(data.notifications);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
      // Update unread count
      const unreadCount = notifications.filter(n => !n.is_read && n.id !== notificationId).length;
      onUnreadCountChange(unreadCount);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(notif => ({ ...notif, is_read: true })));
      onUnreadCountChange(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent notification click when deleting
    }

    try {
      await notificationsAPI.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));

      // Update unread count if the deleted notification was unread
      const deletedNotif = notifications.find(n => n.id === notificationId);
      if (deletedNotif && !deletedNotif.is_read) {
        const newUnreadCount = notifications.filter(n => !n.is_read && n.id !== notificationId).length;
        onUnreadCountChange(newUnreadCount);
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  // Swipe handlers for mobile
  const handleTouchStart = (e: React.TouchEvent, notificationId: number) => {
    setTouchStart(e.targetTouches[0].clientX);
    setSwipeState({ id: notificationId, offset: 0, direction: null });
  };

  const handleTouchMove = (e: React.TouchEvent, notificationId: number) => {
    if (!touchStart || swipeState.id !== notificationId) return;

    const currentTouch = e.targetTouches[0].clientX;
    const offset = currentTouch - touchStart;

    // Determine direction
    const direction = offset < 0 ? 'left' : 'right';

    setSwipeState({ id: notificationId, offset, direction });
  };

  const handleTouchEnd = async (notification: Notification) => {
    const { offset, direction } = swipeState;

    const DELETE_THRESHOLD = -150; // Swipe left 150px to delete
    const UNREAD_THRESHOLD = 150;  // Swipe right 150px to toggle read/unread

    if (direction === 'left' && offset < DELETE_THRESHOLD) {
      // Complete delete action
      await handleDeleteNotification(notification.id);
    } else if (direction === 'right' && offset > UNREAD_THRESHOLD) {
      // Toggle read/unread
      if (notification.is_read) {
        // Mark as unread (we need to implement this API call)
        try {
          await notificationsAPI.markAsRead(notification.id); // For now, just toggle locally
          setNotifications(prev =>
            prev.map(notif =>
              notif.id === notification.id ? { ...notif, is_read: !notif.is_read } : notif
            )
          );
          // Update unread count
          const newUnreadCount = notifications.filter(n =>
            n.id === notification.id ? !notification.is_read : !n.is_read
          ).length;
          onUnreadCountChange(newUnreadCount);
        } catch (error) {
          console.error('Failed to toggle read status:', error);
        }
      } else {
        await handleMarkAsRead(notification.id);
      }
    }

    // Reset swipe state
    setSwipeState({ id: null, offset: 0, direction: null });
    setTouchStart(null);
  };

  const handleToggleReadStatus = async (notificationId: number, currentIsRead: boolean, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    try {
      if (currentIsRead) {
        // Mark as unread - we'll need to implement this endpoint
        await notificationsAPI.markAsRead(notificationId); // Placeholder
      } else {
        await notificationsAPI.markAsRead(notificationId);
      }

      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: !notif.is_read } : notif
        )
      );

      // Update unread count
      const newUnreadCount = notifications.filter(n =>
        n.id === notificationId ? currentIsRead : !n.is_read
      ).length;
      onUnreadCountChange(newUnreadCount);
    } catch (error) {
      console.error('Failed to toggle read status:', error);
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return t('notifications.timeAgo.now');
    if (seconds < 3600) return t('notifications.timeAgo.minutes', { count: Math.floor(seconds / 60) });
    if (seconds < 86400) return t('notifications.timeAgo.hours', { count: Math.floor(seconds / 3600) });
    if (seconds < 604800) return t('notifications.timeAgo.days', { count: Math.floor(seconds / 86400) });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getPhotoUrl = (photoPath?: string) => {
    if (!photoPath) return null;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${baseUrl.replace(/\/api$/, '')}${photoPath}`;
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'friend_request':
        return '👋';
      case 'friend_accepted':
        return '✅';
      case 'temp_location_shared':
        return '📍';
      case 'friend_nearby':
        return '📌';
      case 'friend_in_same_city':
        return '🏙️';
      default:
        return '🔔';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <div className="notification-dropdown-header">
        <h3>{t('notifications.title')}</h3>
        {notifications.some(n => !n.is_read) && (
          <button onClick={handleMarkAllAsRead} className="mark-all-read-btn">
            {t('notifications.markAllAsRead')}
          </button>
        )}
      </div>

      <div className="notification-dropdown-content">
        {loading ? (
          <div className="notification-loading">{t('notifications.loading')}</div>
        ) : notifications.length === 0 ? (
          <div className="notification-empty">
            <span className="notification-empty-icon">🔔</span>
            <p>{t('notifications.empty')}</p>
          </div>
        ) : (
          <>
            {notifications.map(notification => {
              const isThisNotificationSwiped = swipeState.id === notification.id;
              const offset = isThisNotificationSwiped ? swipeState.offset : 0;
              const direction = isThisNotificationSwiped ? swipeState.direction : null;

              // Calculate opacity for fade effect (starts fading at -120px, fully faded at -180px)
              const opacity = isThisNotificationSwiped && direction === 'left' && offset < -120
                ? Math.max(0.3, 1 + (offset + 120) / 60)
                : 1;

              // Background color based on swipe direction
              let backgroundColor = '';
              if (isThisNotificationSwiped) {
                if (direction === 'left' && offset < -50) {
                  backgroundColor = `rgba(231, 76, 60, ${Math.min(0.3, Math.abs(offset) / 500)})`;
                } else if (direction === 'right' && offset > 50) {
                  backgroundColor = `rgba(74, 144, 226, ${Math.min(0.3, offset / 500)})`;
                }
              }

              return (
                <div
                  key={notification.id}
                  className="notification-item-wrapper"
                >
                  {/* Background hint text */}
                  {isThisNotificationSwiped && direction === 'left' && offset < -50 && (
                    <div className="swipe-hint swipe-hint-delete">{t('notifications.delete')}</div>
                  )}
                  {isThisNotificationSwiped && direction === 'right' && offset > 50 && (
                    <div className="swipe-hint swipe-hint-toggle">
                      {notification.is_read ? t('notifications.markAsUnread') : t('notifications.markAsRead')}
                    </div>
                  )}

                  <div
                    className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                    onClick={() => !isThisNotificationSwiped && handleNotificationClick(notification)}
                    onTouchStart={(e) => handleTouchStart(e, notification.id)}
                    onTouchMove={(e) => handleTouchMove(e, notification.id)}
                    onTouchEnd={() => handleTouchEnd(notification)}
                    style={{
                      transform: `translateX(${offset}px)`,
                      opacity,
                      backgroundColor,
                      transition: isThisNotificationSwiped ? 'none' : 'all 0.3s ease',
                    }}
                  >
                    <div className="notification-icon">
                      {notification.related_user_profile_photo ? (
                        <img
                          src={getPhotoUrl(notification.related_user_profile_photo) || undefined}
                          alt=""
                          className="notification-user-photo"
                        />
                      ) : (
                        <span className="notification-type-icon">{getNotificationIcon(notification.type)}</span>
                      )}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{getTimeAgo(notification.created_at)}</div>
                    </div>

                    {/* Unread indicator - Desktop: clickable dot bottom-right */}
                    {!notification.is_read && (
                      <div
                        className="notification-unread-dot desktop-clickable"
                        onClick={(e) => handleToggleReadStatus(notification.id, notification.is_read, e)}
                        title={t('notifications.markAsRead')}
                      ></div>
                    )}

                    {/* Read indicator - Desktop: clickable dot (gray) bottom-right */}
                    {notification.is_read && (
                      <div
                        className="notification-read-dot desktop-clickable"
                        onClick={(e) => handleToggleReadStatus(notification.id, notification.is_read, e)}
                        title={t('notifications.markAsUnread')}
                      ></div>
                    )}

                    {/* Delete button - Desktop only */}
                    <button
                      className="notification-delete-btn desktop-only"
                      onClick={(e) => handleDeleteNotification(notification.id, e)}
                      title={t('notifications.delete')}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {notifications.length > 0 && (
        <div className="notification-dropdown-footer">
          <button onClick={() => { navigate('/notifications'); onClose(); }} className="see-all-btn">
            {t('notifications.seeAll')}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
